import { useState, useRef, useEffect } from 'react';
import type { AppLockInstance } from '@ascend/applock';

interface LockMenuProps {
  lock: AppLockInstance;
}

export function LockMenu({ lock }: LockMenuProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'menu' | 'change' | 'remove'>('menu');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        resetForm();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function resetForm() {
    setMode('menu');
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setError(null);
    setBusy(false);
  }

  function close() {
    setOpen(false);
    resetForm();
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPw.length < 4) {
      setError('New password must be at least 4 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await lock.changePassword(currentPw, newPw);
      close();
    } catch {
      setError('Current password is incorrect.');
      setBusy(false);
    }
  }

  async function handleRemovePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await lock.removePassword(currentPw);
      close();
    } catch {
      setError('Password is incorrect.');
      setBusy(false);
    }
  }

  return (
    <div className="lock-menu-wrapper" ref={ref}>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        title="App lock"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="App lock settings"
      >
        <LockIcon />
      </button>

      {open ? (
        <div className="lock-menu-dropdown" role="menu">
          {mode === 'menu' ? (
            <>
              <div className="lock-menu-header">
                <span className="lock-menu-badge">Encrypted</span>
              </div>
              <button
                type="button"
                className="lock-menu-item"
                onClick={() => {
                  lock.lock('manual');
                  close();
                }}
              >
                Lock now
              </button>
              <button
                type="button"
                className="lock-menu-item"
                onClick={() => {
                  setMode('change');
                  setError(null);
                }}
              >
                Change password…
              </button>
              <button
                type="button"
                className="lock-menu-item lock-menu-danger"
                onClick={() => {
                  setMode('remove');
                  setError(null);
                }}
              >
                Remove password…
              </button>
            </>
          ) : null}

          {mode === 'change' ? (
            <form className="lock-menu-form" onSubmit={handleChangePassword}>
              <h3 className="lock-menu-title">Change password</h3>
              <input
                type="password"
                className="text-input"
                placeholder="Current password"
                value={currentPw}
                autoComplete="current-password"
                autoFocus
                onChange={(e) => setCurrentPw(e.target.value)}
              />
              <input
                type="password"
                className="text-input"
                placeholder="New password"
                value={newPw}
                autoComplete="new-password"
                onChange={(e) => setNewPw(e.target.value)}
              />
              <input
                type="password"
                className="text-input"
                placeholder="Confirm new password"
                value={confirmPw}
                autoComplete="new-password"
                onChange={(e) => setConfirmPw(e.target.value)}
              />
              {error ? <p className="lock-error" role="alert">{error}</p> : null}
              <div className="lock-menu-actions">
                <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={close} disabled={busy}>
                  Cancel
                </button>
              </div>
            </form>
          ) : null}

          {mode === 'remove' ? (
            <form className="lock-menu-form" onSubmit={handleRemovePassword}>
              <h3 className="lock-menu-title">Remove password</h3>
              <p className="lock-description">
                Your data will be decrypted back to cleartext storage. Anyone with
                browser access will be able to read it.
              </p>
              <input
                type="password"
                className="text-input"
                placeholder="Current password"
                value={currentPw}
                autoComplete="current-password"
                autoFocus
                onChange={(e) => setCurrentPw(e.target.value)}
              />
              {error ? <p className="lock-error" role="alert">{error}</p> : null}
              <div className="lock-menu-actions">
                <button type="submit" className="btn btn-danger btn-sm" disabled={busy}>
                  {busy ? 'Removing…' : 'Remove'}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={close} disabled={busy}>
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
